import { ChangeEvent, ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { Formik } from 'formik'
import { keys, isEmpty } from 'lodash'

import { Input } from 'components/Input'
import { Select } from 'components/v2/Select'
import { Tag } from 'components/v2/Tag'
import { TYPE as SITE_TYPE } from 'features/Sites/components/Type/constants'
import { typeMessages as siteTypeMessages } from 'features/Sites/components/Type/messages'
import { getQueryString } from 'utils/route'
import { WITHOUT_USER } from 'resolvers/sites'
import {
  FilterHeader,
  FilterWrapper,
  FilterToggle,
  TagsWrapper,
  FiltersWrapper,
  FiltersContainer,
  FiltersSubmit
} from 'components/Filters'
import { UserAutocomplete } from 'features/Users/components/UserAutocomplete'
import { GTUserSuggestion } from 'gql/types.generated'
import { safeFormatMessage } from 'utils/intl'

const SITE_TYPE_LIST = [SITE_TYPE.MOBILE, SITE_TYPE.FIXED]

const messages = defineMessages({
  selectType: {
    id: 'sites.filters.selectType',
    defaultMessage: 'Select Type'
  },
  selectUser: {
    id: 'sites.filters.selectUser',
    defaultMessage: 'Select User'
  },
  namePlaceholder: {
    id: 'sites.filters.namePlaceholder',
    defaultMessage: 'Site name'
  }
})

const getCashierLabel = (cashier: GTUserSuggestion): string => {
  if (cashier.username) {
    return `${cashier.firstName} ${cashier.lastName} (${cashier.username})`
  }

  if (cashier.firstName) {
    return cashier.firstName
  }

  return ''
}

interface ISitesFiltersProps {
  filters: {
    type: string
    user: GTUserSuggestion
    name: string
  }
}

export const SitesFilters = ({ filters }: ISitesFiltersProps): ReactElement => {
  const navigate = useNavigate()
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): void => {
    showFilters(!isShowingFilters)
  }

  const filterSites = (values: ISitesFiltersProps['filters']): void => {
    const query = {
      type: values.type,
      user: values.user && values.user.id,
      name: values.name
    }

    navigate(`/sites?${getQueryString(query)}`)
  }

  const initialValues: ISitesFiltersProps['filters'] = {
    type: filters.type || '',
    user: filters.user || { id: '' },
    name: filters.name || ''
  }

  return (
    <Formik initialValues={initialValues} onSubmit={filterSites} enableReinitialize>
      {(props): ReactElement => {
        const { handleChange, handleSubmit, setFieldValue, values } = props

        const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>): void => {
          const {
            target: { id, value }
          } = event

          handleChange(event)

          filterSites({
            ...values,
            [id]: value
          })
        }

        const handleFilterDelete = (id: string): void => {
          setFieldValue(id, '')
          filterSites({
            ...values,
            [id]: ''
          })
        }

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <FilterToggle onClick={toggleFilters} />
                  <TagsWrapper>
                    {keys(filters).map((filterKey) => {
                      let label, value
                      const filterValue = filters[filterKey as keyof ISitesFiltersProps['filters']]

                      const hasValue = !isEmpty(filterValue)

                      if (!hasValue) return null

                      switch (filterKey) {
                        case 'type':
                          label = <FormattedMessage id="dictionary.type" defaultMessage="Type" />
                          value = safeFormatMessage({ intl, messages: siteTypeMessages, key: filterValue as string })
                          break
                        case 'user':
                          label = <FormattedMessage id="dictionary.user" defaultMessage="User" />
                          value = getCashierLabel(filterValue as GTUserSuggestion)
                          break
                        case 'name':
                          label = <FormattedMessage id="dictionary.name" defaultMessage="Name" />
                          value = filterValue as string
                          break
                      }

                      if (!value) return null

                      return (
                        <Tag key={filterKey} onDelete={handleFilterDelete.bind(this, filterKey)}>
                          {label}: {value}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <UserAutocomplete
                    placeholder={intl.formatMessage(messages.selectUser)}
                    value={getCashierLabel(values.user)}
                    label={<FormattedMessage id="dictionary.user" defaultMessage="User" />}
                    onChange={(user: GTUserSuggestion): void => {
                      setFieldValue('user', user)

                      filterSites({
                        ...values,
                        user
                      })
                    }}
                    additionalUsers={[WITHOUT_USER]}
                    getItemLabel={getCashierLabel}
                    isAssigned
                  />
                  <Select
                    id="type"
                    value={values.type}
                    onChange={handleSelectChange}
                    label={<FormattedMessage id="dictionary.type" defaultMessage="Type" />}
                  >
                    <option value="">{intl.formatMessage(messages.selectType)}</option>

                    {SITE_TYPE_LIST.map((type) => {
                      return (
                        <option value={type} key={type}>
                          {intl.formatMessage(siteTypeMessages[type])}
                        </option>
                      )
                    })}
                  </Select>
                  <Input
                    id="name"
                    value={values.name}
                    onChange={handleChange}
                    placeholder={intl.formatMessage(messages.namePlaceholder)}
                    label={<FormattedMessage id="dictionary.name" defaultMessage="Name" />}
                  />
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <FiltersSubmit />
          </form>
        )
      }}
    </Formik>
  )
}
